import { Component, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

type Props = {
  children: ReactNode;
  /** changing this value clears a previous error (e.g. new params or a new sim) */
  resetKey?: string | number;
  /** compact inline message instead of a full card */
  inline?: boolean;
};

type State = { error: Error | null; key?: string | number };

/** Keeps one broken simulation (or graph) from taking down the whole page. */
export class SimErrorBoundary extends Component<Props, State> {
  state: State = { error: null, key: undefined };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (state.key !== props.resetKey) return { error: null, key: props.resetKey };
    return null;
  }

  componentDidCatch(error: Error) {
    console.error("[simulation]", error);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.inline) {
      return (
        <p className="flex items-center gap-2 py-4 text-[11.5px] text-muted-foreground">
          <AlertTriangle className="size-3.5 shrink-0 text-orange-400" />
          Couldn&apos;t plot these values — try adjusting a slider.
        </p>
      );
    }

    return (
      <div className="glass rounded-[20px] border border-orange-400/25 p-4">
        <p className="flex items-center gap-2 text-[13px] font-semibold">
          <AlertTriangle className="size-4 shrink-0 text-orange-400" />
          This simulation hit a snag
        </p>
        <p className="mt-1 text-[12px] text-muted-foreground">
          The values you chose produced an invalid result. Reset the controls and try again.
        </p>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          className="mt-3 rounded-full [background-image:var(--gradient-brand)] px-4 py-2 text-[12px] font-semibold text-primary-foreground transition-transform active:scale-95"
        >
          Try again
        </button>
      </div>
    );
  }
}
